import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { LOGO_URL, BUSINESS, SERVICE_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABEL = Object.fromEntries(SERVICE_OPTIONS.map((s) => [s.value, s.label]));

const STATUS_META = {
  new:       { label: "New",       cls: "bg-brand-red/10 text-brand-red border-brand-red/30",    dot: "bg-brand-red" },
  contacted: { label: "Contacted", cls: "bg-blue-50 text-blue-700 border-blue-200",              dot: "bg-blue-500" },
  quoted:    { label: "Quoted",    cls: "bg-orange-50 text-orange-700 border-orange-200",        dot: "bg-orange-500" },
  closed:    { label: "Closed",    cls: "bg-slate-100 text-slate-600 border-slate-200",          dot: "bg-slate-400" },
};

const STATUSES = ["new", "contacted", "quoted", "closed"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

export default function AdminDashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, quoted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeLeadId, setActiveLeadId] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers, params }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(listRes.data || []);
      setStats(statsRes.data || stats);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Could not load leads.");
      if (err?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, statusFilter, search, onLogout]);

  useEffect(() => { load(); }, [load]);

  const updateLead = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/admin/quotes/${id}`, patch, { headers });
      setLeads((prev) => prev.map((l) => (l.id === id ? res.data : l)));
      // Refresh stats after a status change
      if ("status" in patch) {
        try {
          const statsRes = await axios.get(`${API}/admin/stats`, { headers });
          setStats(statsRes.data);
        } catch { /* ignore */ }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Could not update lead.");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { headers });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (activeLeadId === id) setActiveLeadId(null);
      // Refresh stats
      try {
        const statsRes = await axios.get(`${API}/admin/stats`, { headers });
        setStats(statsRes.data);
      } catch { /* ignore */ }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Could not delete lead.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/quotes/export.csv`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `yourphoneplug-leads-${ts}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Could not export CSV.");
    }
  };

  const activeLead = leads.find((l) => l.id === activeLeadId) || null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BUSINESS.name} className="w-10 h-10 rounded-xl" />
            <div>
              <div className="font-display font-black text-brand-navy leading-none">Lead Admin</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{BUSINESS.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-brand-red px-3 py-2 rounded-full border border-slate-200 bg-white transition-colors disabled:opacity-60"
            >
              <Icons.RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={exportCsv}
              data-testid="admin-export-csv-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full bg-brand-navy hover:bg-brand-navy-dark text-white transition-colors"
            >
              <Icons.Download className="w-3.5 h-3.5" />
              Export CSV
            </button>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-brand-red px-3 py-2 rounded-full"
            >
              <Icons.LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
          <StatCard label="Total Leads" value={stats.total} icon="Inbox" tone="navy" testid="admin-stat-total" />
          {STATUSES.map((s) => (
            <StatCard
              key={s}
              label={STATUS_META[s].label}
              value={stats[s] ?? 0}
              icon={s === "new" ? "Sparkles" : s === "contacted" ? "Phone" : s === "quoted" ? "MessageSquareQuote" : "CheckCircle2"}
              tone={s}
              testid={`admin-stat-${s}`}
            />
          ))}
        </div>

        {/* Filters */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-3 md:p-4 shadow-sm flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Icons.Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search-input"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-red focus:bg-white transition-all"
              placeholder="Search by name, phone, model, or issue…"
            />
          </div>
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 border border-slate-200 overflow-x-auto" role="tablist">
            {[{ value: "all", label: "All" }, ...STATUSES.map((s) => ({ value: s, label: STATUS_META[s].label }))].map((opt) => {
              const active = statusFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setStatusFilter(opt.value)}
                  data-testid={`admin-filter-${opt.value}`}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                    active ? "bg-brand-red text-white shadow-sm" : "text-slate-600 hover:bg-white"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="mt-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Lead list */}
        <div className="mt-5">
          {loading && leads.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500" data-testid="admin-leads-loading">
              Loading leads…
            </div>
          ) : leads.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="admin-leads-empty">
              <Icons.InboxIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="font-display font-bold text-brand-navy">No leads match your filters.</div>
              <p className="text-xs text-slate-500 mt-1">Try clearing the search or switching status filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3" data-testid="admin-leads-list">
              {leads.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  onOpen={() => setActiveLeadId(lead.id)}
                  onChangeStatus={(status) => updateLead(lead.id, { status })}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {activeLead && (
        <LeadDetailDrawer
          lead={activeLead}
          onClose={() => setActiveLeadId(null)}
          onSave={(patch) => updateLead(activeLead.id, patch)}
          onDelete={() => deleteLead(activeLead.id)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon, tone, testid }) {
  const Icon = Icons[icon] || Icons.Inbox;
  const toneCls = {
    navy:      "bg-brand-navy text-white",
    new:       "bg-brand-red text-white",
    contacted: "bg-blue-500 text-white",
    quoted:    "bg-orange-500 text-white",
    closed:    "bg-slate-400 text-white",
  }[tone] || "bg-slate-200 text-brand-navy";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-xl grid place-items-center ${toneCls}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <div className="mt-3 font-display text-3xl font-black text-brand-navy tabular-nums">{value}</div>
    </div>
  );
}

function LeadRow({ lead, onOpen, onChangeStatus }) {
  const meta = STATUS_META[lead.status] || STATUS_META.new;
  return (
    <div
      data-testid={`admin-lead-${lead.id}`}
      className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <button
          type="button"
          onClick={onOpen}
          className="text-left flex-1 min-w-0"
          data-testid={`admin-lead-open-${lead.id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${meta.cls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
            <span className="font-display font-extrabold text-brand-navy text-base truncate">{lead.name}</span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-xs text-slate-500 tabular-nums">{fmtDate(lead.created_at)}</span>
          </div>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
            <span className="font-medium text-brand-navy">{lead.phone}</span>
            <span className="text-slate-300">|</span>
            <span>{lead.device_type} {lead.device_model}</span>
            <span className="text-slate-300">|</span>
            <span className="font-semibold text-brand-red">{SERVICE_LABEL[lead.service_needed] || lead.service_needed}</span>
            <span className="text-slate-300">|</span>
            <span className="capitalize text-slate-500">{lead.contact_pref}</span>
          </div>
          <p className="mt-2 text-sm text-slate-700 line-clamp-2">{lead.issue}</p>
        </button>

        <div className="flex items-center gap-2 shrink-0">
          <a
            href={`tel:${lead.phone}`}
            data-testid={`admin-lead-call-${lead.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full bg-brand-red text-white hover:brightness-110 transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <Icons.Phone className="w-3.5 h-3.5" />
            Call
          </a>
          <select
            value={lead.status || "new"}
            onChange={(e) => onChangeStatus(e.target.value)}
            data-testid={`admin-lead-status-${lead.id}`}
            className="text-xs font-bold bg-slate-50 border border-slate-200 rounded-full px-3 py-2 text-brand-navy focus:outline-none focus:border-brand-red"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function LeadDetailDrawer({ lead, onClose, onSave, onDelete }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [status, setStatus] = useState(lead.status || "new");
  const [saving, setSaving] = useState(false);

  // Re-sync if a different lead is opened
  useEffect(() => {
    setNotes(lead.notes || "");
    setStatus(lead.status || "new");
  }, [lead.id, lead.notes, lead.status]);

  const dirty = notes !== (lead.notes || "") || status !== (lead.status || "new");

  const save = async () => {
    setSaving(true);
    await onSave({ status, notes });
    setSaving(false);
  };

  return (
    <div
      data-testid="admin-lead-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-testid="admin-drawer-overlay"
        className="absolute inset-0 bg-brand-navy/50 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Lead Detail</div>
            <h2 className="font-display text-xl font-black text-brand-navy truncate">{lead.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-drawer-close"
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 grid place-items-center transition-colors"
            aria-label="Close drawer"
          >
            <Icons.X className="w-4 h-4 text-brand-navy" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <Detail label="Phone">
            <a href={`tel:${lead.phone}`} className="font-bold text-brand-red hover:underline">
              {lead.phone}
            </a>
          </Detail>
          <Detail label="Device">{lead.device_type} {lead.device_model}</Detail>
          <Detail label="Service">{SERVICE_LABEL[lead.service_needed] || lead.service_needed}</Detail>
          <Detail label="Preferred Contact"><span className="capitalize">{lead.contact_pref}</span></Detail>
          <Detail label="Submitted">{fmtDate(lead.created_at)}</Detail>
          <Detail label="Issue">
            <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">{lead.issue}</p>
          </Detail>

          <div>
            <span className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
              Status
            </span>
            <div className="grid grid-cols-4 gap-1.5 p-1 rounded-xl bg-slate-100 border border-slate-200" role="radiogroup">
              {STATUSES.map((s) => {
                const meta = STATUS_META[s];
                const active = status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setStatus(s)}
                    data-testid={`admin-drawer-status-${s}`}
                    className={`text-xs font-bold py-2 rounded-lg transition-all ${
                      active ? "bg-brand-red text-white shadow-sm" : "text-slate-600 hover:bg-white"
                    }`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
              Internal Notes
            </span>
            <textarea
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="admin-drawer-notes"
              placeholder="Called and left voicemail at 2:14 PM…"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-red focus:bg-white transition-all resize-none"
            />
          </label>

          <div className="flex items-center justify-between pt-2 gap-3">
            <button
              type="button"
              onClick={onDelete}
              data-testid="admin-drawer-delete"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700 px-3 py-2 rounded-full"
            >
              <Icons.Trash2 className="w-3.5 h-3.5" />
              Delete lead
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="admin-drawer-save"
              className="inline-flex items-center gap-2 font-bold rounded-full px-5 py-2.5 bg-brand-red hover:brightness-110 text-white text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.Save className="w-4 h-4" />}
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-brand-navy">{children}</div>
    </div>
  );
}
